// Shared layer helpers: severity → RGBA, MIL-STD-2525 frame mapping, and
// the icon-atlas spec used by every signal-icon layer. Internal — not part
// of the package public API.
//
// Color decisions delegate to ../colors.ts. Adding a literal RGBA here is
// a review violation — extend the MapPalette returned by getMapPalette().

import type { intel as i } from '@eidollon/contracts';
import { type RGBA, severityWeight } from '../colors.js';

export type { RGBA };

export { severityWeight };

/** MIL-STD-2525 frame shape per affiliation. Frame fill = severity color
 *  (set by the layer caller); shape varies with affiliation. We default to
 *  `unknown` (clover) when no affiliation is known. Phase D wires real
 *  affiliation onto IntelSignal; for now we infer from signal type. */
export type FrameShape = 'diamond' | 'square' | 'clover' | 'oval' | 'flame';

export function frameShapeFor(s: i.IntelSignal): FrameShape {
  // Hostile: sanctions targets + ACLED/GDELT armed events.
  if (s.signal_type === 'SAN' || s.signal_type === 'CFL') return 'square';
  // Friendly: domestic obligations + policy.
  if (s.signal_type === 'OBL' || s.signal_type === 'POL') return 'diamond';
  // Wildfire gets a literal flame silhouette so it reads as fire at a glance —
  // shape is the first read, before the amber-orange severity ramp. A field of
  // flames must never be confusable with the oval geophysical markers (quakes).
  if (s.signal_type === 'FIR') return 'flame';
  // Neutral: weather + seismic + supply (geophysical / civilian) + news.
  if (
    s.signal_type === 'WEA' ||
    s.signal_type === 'SEI' ||
    s.signal_type === 'SUP' ||
    s.signal_type === 'NEW'
  )
    return 'oval';
  // Anomaly + aviation start as unknown.
  return 'clover';
}

/** Atlas key — feeds deck.gl IconLayer's `iconAtlas` + `iconMapping`. The
 *  same SVG sprite (32×32, white-fill, transparent bg) is masked by the
 *  caller's `getColor` so any single sprite can render in any severity color.
 *
 *  Phase C-1 ships an inline data-URI atlas so we have zero runtime asset
 *  fetches — Phase D promotes this to a proper `apps/terminal/public/atlas`
 *  bundle when more sprites land. */
const ICON_SIZE = 32;
// Atlas is one 32px-tall row of 32px cells. Five sprites → 160px wide.
const ATLAS_WIDTH = ICON_SIZE * 5;
const ATLAS_HEIGHT = ICON_SIZE;

const SVG_FRAME = `data:image/svg+xml;utf8,${encodeURIComponent(
  [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${ATLAS_WIDTH}" height="${ATLAS_HEIGHT}" viewBox="0 0 ${ATLAS_WIDTH} ${ATLAS_HEIGHT}">`,
    // diamond  (0,0)
    '<path d="M16 4 L28 16 L16 28 L4 16 Z" fill="white"/>',
    // square   (32,0)
    '<rect x="36" y="4" width="24" height="24" fill="white"/>',
    // clover   (64,0) — four-leaf approximation
    '<path d="M80 4 a8 8 0 0 1 0 16 a8 8 0 0 1 -16 0 a8 8 0 0 1 0 -16 a8 8 0 0 1 16 0 z M80 20 a8 8 0 0 1 0 16 a8 8 0 0 1 -16 0 a8 8 0 0 1 0 -16 a8 8 0 0 1 16 0 z" fill="white"/>',
    // oval     (96,0)
    '<ellipse cx="112" cy="16" rx="14" ry="10" fill="white"/>',
    // flame    (128,0) — teardrop silhouette: pointed tip at top, widening to
    // a rounded base, with a small inner lick on the left so it reads as fire
    // (not a raindrop) even at the smallest marker size. Cell origin x=128, so
    // x is centred on 144 within the 32px cell.
    '<path d="M144 3 C152 12 156 16 156 22 A12 12 0 0 1 132 22 C132 17 136 15 138 11 C139 15 141 16 143 16 C144 11 141 7 144 3 Z" fill="white"/>',
    '</svg>',
  ].join(''),
)}`;

export const ICON_ATLAS = SVG_FRAME;

export const ICON_MAPPING: Record<
  FrameShape,
  { x: number; y: number; width: number; height: number; mask: boolean }
> = {
  diamond: { x: 0, y: 0, width: ICON_SIZE, height: ICON_SIZE, mask: true },
  square: { x: 32, y: 0, width: ICON_SIZE, height: ICON_SIZE, mask: true },
  clover: { x: 64, y: 0, width: ICON_SIZE, height: ICON_SIZE, mask: true },
  oval: { x: 96, y: 0, width: ICON_SIZE, height: ICON_SIZE, mask: true },
  flame: { x: 128, y: 0, width: ICON_SIZE, height: ICON_SIZE, mask: true },
};

/** Filter rows that have a Geom — every renderer needs `[lng, lat]`. */
export function withGeom<T extends i.IntelSignal>(rows: readonly T[]): T[] {
  const out: T[] = [];
  for (const r of rows) if (r.geom) out.push(r);
  return out;
}

/** H3 resolution that matches the design doc's zoom→res ramp (res 4 at z=4,
 *  res 6 at z=8, res 8 at z=12). Linear interpolation, clamped to [3, 9]. */
export function h3ResForZoom(zoom: number): number {
  const r = Math.round(2 + zoom * 0.5);
  return Math.max(3, Math.min(9, r));
}
